import { getDefaultStore } from 'jotai';

import {
  type CopilotPrechartRequest,
  type PrechartSessionTarget,
} from '../../session-list/session-list-host';
import { isContextExtractionEnabledAtom, isPrechartingEnabledAtom } from '../atoms';
import type { ScheduleImportOrigin, ScheduledSessionDraft } from '../types';

/** get/set for the copilot request atom, called outside React. */
export interface CopilotPrechartPort {
  getRequest: () => CopilotPrechartRequest | null;
  setRequest: (request: CopilotPrechartRequest | null) => void;
}

interface CorrelatedDraft {
  importOrigin?: ScheduleImportOrigin;
}

interface CreatedSessionTarget {
  session_id: string;
  patient?: { name?: string | null } | null;
}

let prechartCorrelationSequence = 0;

const createPrechartCorrelationId = (): string =>
  `copilot-prechart-${Date.now()}-${++prechartCorrelationSequence}`;

export const correlationIdFromOrigin = (
  origin: ScheduleImportOrigin | undefined
): string | undefined => (origin?.kind === 'copilot' ? origin.correlationId : undefined);

export const copilotOriginFromDrafts = (
  drafts: readonly CorrelatedDraft[]
): Extract<ScheduleImportOrigin, { kind: 'copilot' }> | undefined => {
  const first = drafts[0]?.importOrigin;
  if (first?.kind !== 'copilot') return undefined;
  return drafts.every(
    (draft) =>
      draft.importOrigin?.kind === 'copilot' &&
      draft.importOrigin.correlationId === first.correlationId
  )
    ? first
    : undefined;
};

export const isPrechartRequestExpired = (
  request: CopilotPrechartRequest,
  ttlMs: number,
  now = Date.now()
): boolean => now - request.requestedAt >= ttlMs;

export const isMatchingPrechartImport = (
  request: CopilotPrechartRequest | null,
  drafts: readonly CorrelatedDraft[],
  ttlMs: number,
  now = Date.now()
): boolean =>
  request !== null &&
  !isPrechartRequestExpired(request, ttlMs, now) &&
  drafts.some(
    (draft) =>
      draft.importOrigin?.kind === 'copilot' &&
      draft.importOrigin.correlationId === request.correlationId
  );

export const toPrechartTargets = (
  sessions: readonly CreatedSessionTarget[]
): PrechartSessionTarget[] =>
  sessions
    .map((session) => ({
      sessionId: session.session_id,
      patientName: session.patient?.name ?? '',
    }))
    .filter((target) => Boolean(target.sessionId) && Boolean(target.patientName.trim()));

/**
 * Keep only created sessions that correspond to copilot-correlated drafts for
 * `correlationId`. Prevents a mixed selected batch (copilot + manual) from
 * driving automated EHR pre-charting for sessions outside the original intent.
 */
export const filterSessionsForCopilotCorrelation = <T extends CreatedSessionTarget>(
  sessions: readonly T[],
  drafts: readonly (CorrelatedDraft & { patient_name?: string | null })[],
  correlationId: string
): T[] => {
  const correlatedNames = new Set(
    drafts
      .filter(
        (draft) =>
          draft.importOrigin?.kind === 'copilot' &&
          draft.importOrigin.correlationId === correlationId &&
          Boolean(draft.patient_name?.trim())
      )
      .map((draft) => draft.patient_name!.trim().toLowerCase())
  );
  if (correlatedNames.size === 0) return [];
  return sessions.filter((session) =>
    correlatedNames.has((session.patient?.name ?? '').trim().toLowerCase())
  );
};

export const clearMatchingPrechartRequest = (
  current: CopilotPrechartRequest | null,
  claimedCorrelationId: string | undefined
): CopilotPrechartRequest | null =>
  current?.correlationId === claimedCorrelationId ? null : current;

export const correlationIdForDrafts = (
  drafts: readonly CorrelatedDraft[]
): string | undefined => correlationIdFromOrigin(copilotOriginFromDrafts(drafts));

const draftNameKey = (name: string | null | undefined): string =>
  (name ?? '')
    .toLowerCase()
    .replace(/[\s,]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .toSorted()
    .join(' ');

/**
 * Keep the clinician's in-preview checkboxes when assist replaces the draft
 * list. Match by name+time, then unique name on both sides (time corrections).
 * New or duplicate-name rows keep the incoming selection.
 */
export const preserveDraftSelection = <T extends ScheduledSessionDraft>(
  incoming: readonly T[],
  current: readonly ScheduledSessionDraft[]
): T[] => {
  if (current.length === 0) return incoming.map((draft) => ({ ...draft }));

  const byNameTime = new Map<string, boolean>();
  const byName = new Map<string, boolean[]>();
  for (const draft of current) {
    const name = draftNameKey(draft.patient_name);
    byNameTime.set(`${name}|${draft.datetime ?? ''}`, !!draft.is_selected);
    const named = byName.get(name) ?? [];
    named.push(!!draft.is_selected);
    byName.set(name, named);
  }

  const incomingNameCount = new Map<string, number>();
  for (const draft of incoming) {
    const name = draftNameKey(draft.patient_name);
    incomingNameCount.set(name, (incomingNameCount.get(name) ?? 0) + 1);
  }

  return incoming.map((draft) => {
    const name = draftNameKey(draft.patient_name);
    const exact = byNameTime.get(`${name}|${draft.datetime ?? ''}`);
    if (exact !== undefined) return { ...draft, is_selected: exact };
    const named = byName.get(name);
    // Time correction only: the name must be unique on both sides. A second
    // incoming Jane must keep its own checkbox, not inherit the first Jane's.
    if (
      named?.length === 1 &&
      incomingNameCount.get(name) === 1 &&
      named[0] !== undefined
    ) {
      return { ...draft, is_selected: named[0] };
    }
    return { ...draft };
  });
};

/**
 * Stamp a copilot schedule handoff onto the request atom and every draft.
 * Returns the drafts with `importOrigin` set — callers should write those into
 * the preview atom.
 */
export const claimCopilotImport = <T extends ScheduledSessionDraft>({
  drafts,
  ehrWindowHandle,
  requestedAt = Date.now(),
  setRequest,
}: {
  drafts: readonly T[];
  ehrWindowHandle?: number;
  requestedAt?: number;
  setRequest: CopilotPrechartPort['setRequest'];
}): Array<T & { importOrigin: Extract<ScheduleImportOrigin, { kind: 'copilot' }> }> => {
  const correlationId = createPrechartCorrelationId();
  const importOrigin: Extract<ScheduleImportOrigin, { kind: 'copilot' }> = {
    kind: 'copilot',
    correlationId,
    ...(ehrWindowHandle != null ? { ehrWindowHandle } : {}),
  };
  setRequest({
    correlationId,
    requestedAt,
    ...(ehrWindowHandle != null ? { ehrWindowHandle } : {}),
  });
  return drafts.map((draft) => ({ ...draft, importOrigin }));
};

/**
 * Clear a matching copilot prechart request and reset import-modal opt-in
 * toggles. Shared by discard, empty-draft cancel, and any path that abandons
 * a correlated preview so the next review starts opted out.
 */
export const resetPrechartImportUiState = (
  drafts: readonly CorrelatedDraft[] = [],
  port: CopilotPrechartPort
): void => {
  const correlationId = correlationIdForDrafts(drafts);
  port.setRequest(clearMatchingPrechartRequest(port.getRequest(), correlationId));
  const store = getDefaultStore();
  store.set(isPrechartingEnabledAtom, false);
  store.set(isContextExtractionEnabledAtom, false);
};
